import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Download, Inbox } from "lucide-react";
import axios from "axios";
import { API, COMMODITIES, PAYMENT_INSTRUMENTS } from "@/lib/constants";
import Reveal from "@/components/Reveal";

const selectCls =
  "border border-white/15 bg-[#0d223f] px-4 py-2.5 text-sm text-white outline-none transition-colors focus:border-[#4a6cf7]";

const AdminInquiries = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commodity, setCommodity] = useState("");
  const [payment, setPayment] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (commodity) params.commodity = commodity;
      if (payment) params.payment_instrument = payment;
      const { data } = await axios.get(`${API}/inquiries`, { params });
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [commodity, payment]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCsv = () => {
    const headers = ["reference", "type", "company_name", "email", "phone", "commodity", "quantity_mt", "payment_instrument", "incoterm", "destination_port", "created_at"];
    const rows = items.map((i) =>
      headers
        .map((h) => `"${String((h === "type" ? i.inquiry_type : i[h]) ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "ore-source-inquiries.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <main data-testid="admin-inquiries-page" className="pt-32">
      <section className="mx-auto max-w-7xl px-5 pb-24 lg:px-8">
        <Reveal>
          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <p className="font-mono2 text-xs uppercase tracking-[0.35em] text-[#9db4ff]">
                Internal Trade Desk
              </p>
              <h1 className="font-display mt-3 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                Inquiries Portal
              </h1>
            </div>
            <div className="flex flex-wrap gap-3">
              <select data-testid="filter-commodity-select" value={commodity} onChange={(e) => setCommodity(e.target.value)} className={selectCls}>
                <option value="">All Commodities</option>
                {COMMODITIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
              <select data-testid="filter-payment-select" value={payment} onChange={(e) => setPayment(e.target.value)} className={selectCls}>
                <option value="">All Instruments</option>
                {PAYMENT_INSTRUMENTS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <button
                data-testid="inquiries-refresh-button"
                onClick={load}
                className="flex items-center gap-2 border border-[#4a6cf7]/50 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-[#9db4ff] transition-colors hover:bg-[#4a6cf7] hover:text-white"
              >
                <RefreshCw className="h-4 w-4" /> Refresh
              </button>
              <button
                data-testid="inquiries-export-button"
                onClick={exportCsv}
                className="flex items-center gap-2 border border-white/20 px-4 py-2.5 font-display text-xs font-semibold uppercase tracking-[0.15em] text-slate-300 transition-colors hover:border-[#4a6cf7] hover:text-[#9db4ff]"
              >
                <Download className="h-4 w-4" /> Export CSV
              </button>
            </div>
          </div>
        </Reveal>

        <div className="mt-10" data-testid="inquiries-list">
          {loading ? (
            <p className="py-20 text-center font-mono2 text-sm uppercase tracking-[0.3em] text-slate-500">
              Loading inquiries…
            </p>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-4 border border-white/10 bg-[#0d223f] py-20">
              <Inbox className="h-10 w-10 text-slate-600" />
              <p className="font-mono2 text-sm uppercase tracking-[0.3em] text-slate-500" data-testid="inquiries-empty-state">
                No inquiries recorded yet
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto border border-white/10">
              <table className="w-full min-w-[900px] text-left text-sm">
                <thead>
                  <tr className="border-b border-[#4a6cf7]/25 bg-[#0d223f] font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#9db4ff]">
                    <th className="px-5 py-4">Reference</th>
                    <th className="px-5 py-4">Type</th>
                    <th className="px-5 py-4">Company</th>
                    <th className="px-5 py-4">Commodity</th>
                    <th className="px-5 py-4">Qty (MT/mo)</th>
                    <th className="px-5 py-4">Instrument</th>
                    <th className="px-5 py-4">Port / Incoterm</th>
                    <th className="px-5 py-4">Date</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                  {items.map((i) => (
                    <tr key={i.id} data-testid={`inquiry-row-${i.reference}`} className="bg-[#0a192f] transition-colors hover:bg-[#0d223f]">
                      <td className="px-5 py-4 font-mono2 text-xs text-[#9db4ff]">{i.reference}</td>
                      <td className="px-5 py-4">
                        <span className={`border px-2 py-1 text-[10px] font-semibold uppercase tracking-widest ${i.inquiry_type === "rfq" ? "border-[#4a6cf7]/50 text-[#9db4ff]" : "border-slate-500/50 text-slate-400"}`}>
                          {i.inquiry_type}
                        </span>
                      </td>
                      <td className="px-5 py-4 text-white">
                        {i.company_name}
                        <span className="block text-xs text-slate-500">{i.email}</span>
                      </td>
                      <td className="px-5 py-4 text-slate-300">{i.commodity || "—"}</td>
                      <td className="px-5 py-4 text-slate-300">{i.quantity_mt || "—"}</td>
                      <td className="px-5 py-4 text-slate-300">{i.payment_instrument || "—"}</td>
                      <td className="px-5 py-4 text-slate-300">
                        {i.destination_port || "—"}
                        {i.incoterm ? ` · ${i.incoterm.split(" ")[0]}` : ""}
                      </td>
                      <td className="px-5 py-4 text-xs text-slate-500">
                        {new Date(i.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default AdminInquiries;
